import { useSearch } from '@tanstack/react-location'
import { useFormik } from 'formik'
import ActionSuccessModal from 'MediaManager/components/ActionSuccessModal'
import Input from 'MediaManager/components/Form/Input'
import ScreenLayout from 'MediaManager/layouts/ScreenLayout'
import { getLivingVariants, getLivingVariantTypes, upsertLivingVariant } from 'MediaManager/services/variants'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Button from 'shared/components/Button'
import Checkbox from 'shared/components/Checkbox'
import Loader from 'shared/components/Loader'
import Select from 'shared/components/Select'
import { appEnvPrefix, isProductionEnv } from 'shared/helpers/general'
import { useRequest } from 'shared/hooks/useRequest'
import { errorMessages } from 'shared/settings'

import * as Yup from 'yup'

const flags = [
  {
    label: 'Exit Intent',
    name: 'exit_intent',
  },
  {
    label: 'Vendor Connect',
    name: 'vendor_connect',
  },
  {
    label: 'Hide vendor name',
    name: 'hide_vendor_name',
  },
  {
    label: 'Hide prices',
    name: 'hide_prices',
  },
  {
    label: 'Hide disclaimer',
    name: 'hide_disclaimer',
  },
  {
    label: 'Show assets package price',
    name: 'show_assets_package_price',
  },
]

const domains = ['offices--ENV--.sparx.ai', 'cort-widget--ENV--.sparx.ai', 'living--ENV--.sparx.ai', 'brodsky--ENV--.sparx.ai']

function EditLivingVariantsScreen(props) {
  const { id } = useSearch()

  const getLivingVariantTypesRequest = useCallback(async () => {
    const { data } = await getLivingVariantTypes()
    return data.living_variant_types
  }, [])

  const { data: variantTypes, loading: variantTypesLoading } = useRequest(getLivingVariantTypesRequest)

  const [resultValue, setResultValue] = useState()

  const validationSchema = Yup.object().shape({
    variant_type_id: Yup.number().required(errorMessages.required),
    name: Yup.string().required(errorMessages.required),
  })

  const formik = useFormik({
    initialValues: {},
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema,
    initialTouched: false,
    validateOnMount: true,
    onSubmit: async () => {
      try {
        const payload = {
          id: id ? Number(id) : undefined,
          living_variant_type_id: formik.values.variant_type_id,
          configuration: {
            ...flags.reduce((acc, val) => ({ ...acc, [val.name]: formik.values[val.name] || false }), {}),
            name: formik.values.name,
            domain: formik.values.domain || null,
          },
        }
        await upsertLivingVariant(payload)
        setResultValue('success')
      } catch (error) {
        console.error(error)
        setResultValue('error')
      }
    },
  })

  useEffect(() => {
    ;(async () => {
      if (id) {
        const { data } = await getLivingVariants()
        const variant = data.living_variants.find((v) => v.id === Number(id))
        if (variant) {
          formik.setValues({
            variant_type_id: variant.living_variant_type.id,
            ...variant.configuration,
          })
        }
      }
    })()
  }, [id])

  function getFieldError(field) {
    return formik.errors[field] && formik.touched[field] ? formik.errors[field] : null
  }

  const domainOptions = useMemo(() => {
    return domains
      .map((d) => d.replace('--ENV--', isProductionEnv() ? '' : `-${appEnvPrefix()}`))
      .map((domain) => ({
        value: domain,
        label: domain,
      }))
  }, [])

  return (
    <ScreenLayout title={!id ? 'Create sparx living variant' : 'Edit living variant'}>
      {resultValue && (
        <ActionSuccessModal
          action={`Level`}
          successText="has been successfully upserted."
          errorText="upserting failed. Please try again."
          state={resultValue}
          error={'Please try again later.'}
          skipConfirm
          skipBack
          onClose={() => {
            if (resultValue === 'success') {
              window.history.back()
            } else {
              setResultValue(null)
            }
          }}
        />
      )}
      <form onSubmit={formik.handleSubmit} className="w-full">
        <Input
          label="Variant name"
          required
          name="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        <div className="grid grid-cols-3 mt-[16px] gap-[16px]">
          {flags.map((s) => (
            <div className="flex items-center" key={s.name}>
              <Checkbox
                name={s.id}
                checked={formik.values[s.name]}
                onChange={() => {
                  formik.setFieldValue(s.name, !formik.values[s.name])
                }}
              />
              <div className="ml-3 truncate">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="mt-[16px]">
          <Select
            onChange={(val) => formik.setFieldValue('domain', val.value)}
            value={domainOptions.find((line) => line.value === formik.values.domain) || ''}
            options={domainOptions}
            label="Domain (optional)"
            placeholder="Select domain"
          />
        </div>
        <div className="mt-[16px]">
          <div className="text-[14px] opacity-50">Select disclaimer</div>
          <div className="mt-[16px] border-solid border-[1px] border-[#E0E0E0] rounded-[8px] p-[16px]">
            {variantTypesLoading && <Loader />}
            {!variantTypesLoading && (
              <div className="grid grid-cols-3 gap-[16px]">
                {variantTypes.map((v) => (
                  <div>
                    <Button
                      size="small"
                      rounded
                      className="mt-[16px]"
                      key={v.id}
                      onClick={() => {
                        formik.setFieldValue('variant_type_id', v.id)
                      }}
                      variant={v.id === formik.values.variant_type_id ? 'primary' : 'secondary'}
                    >
                      {v.type}
                    </Button>
                    <div>
                      {v.disclaimer && (
                        <div className="text-sm text-gray-500 mt-[8px]">
                          <ul className="list-disc pl-[16px]">
                            {JSON.parse(v.disclaimer).map((text, index) => (
                              <li key={index} className="mt-[16px] text-black">
                                {text}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end mt-[16px]">
          <Button rounded variant="secondary" onClick={() => window.history.back()}>
            Cancel
          </Button>
          <Button className="ml-[16px]" rounded disabled={!formik.isValid} loading={formik.isSubmitting} type="submit">
            {id ? 'Edit' : 'Create'} variant
          </Button>
        </div>
      </form>
    </ScreenLayout>
  )
}

export default EditLivingVariantsScreen
